import { Howl } from 'howler';
import omit from 'lodash/omit';

import Config from '../../app.config';

import { LocalPlayer } from './local';
import { ActionHandler } from 'middlewares/orchestrator';
import { ChannelState, DefaultChannelVolume, EventType, failItem, QueueItem, stopItem, unloadItem } from '../slices/local/orchestrator';
import { type Progress } from './player';
import { getSentry } from '../helpers/get-sentry';

import logo from '../styles/assets/images/logo.png';
export class WebPlayer extends LocalPlayer {
  private songPlayers: Record<number, Howl> = {};
  private pendingUnloads = new Set<number>();

  public override progress(index: number): Progress {
    const howl = this.getHowl(index, false);
    if (howl) {
      return {
        percent: 100 * (howl.seek() / howl.duration()),
        seconds: howl.seek()
      }
    }
    return {
      percent: 0,
      seconds: 0
    }
  }

  private getHowl(index: number, create: boolean = true): Howl | null {
    if (this.songPlayers[index] !== undefined) {
      return this.songPlayers[index];
    }

    if (!create) {
      return null;
    }

    const item = this.getQueueItem(index);

    if (!item?.destUri) {
      return null;
    }

    const getSanitizedItem = (): Omit<QueueItem, 'events'> =>
      omit(item, ['events']);
    const getSanitizedState = (): Omit<ChannelState, 'queue' | 'cubeEvents'> =>
      omit(this.state, ['queue', 'cubeEvents']);

    const songPlayer = new Howl({
      src: item.destUri,
      format: 'mp3',
      html5: true,
      autoplay: false,
      preload: true,
      mute: this.state?.muted,
      volume: 0.01,
      xhr: {
        withCredentials: true
      },
      onloaderror: (soundId, error) => {
        if (this.songPlayers[index] !== songPlayer) {
          return;
        }

        console.error('Load error', soundId, error);

        getSentry().captureMessage('Web load error', {
          tags: {
            ...getSanitizedItem(),
            ...getSanitizedState(),
            soundId,
            error: JSON.stringify(error)
          }
        });

        this.actionQueue.push(async dispatch => {
          dispatch(failItem({
            ...this.selector,
            index,
            errorType: EventType.ErrorHowlLoad
          }));
        });
      },
      onplayerror: (soundId, error) => {
        if (this.songPlayers[index] !== songPlayer) {
          return;
        }

        console.error('Play error', soundId, error);

        getSentry().captureMessage('Web play error', {
          tags: {
            ...getSanitizedItem(),
            ...getSanitizedState(),
            soundId,
            error: JSON.stringify(error)
          }
        });

        // @todo can only happen/work on mobile devices and needs to be tested
        /*songPlayer.once('unlock', () => {
          songPlayer.play();
        });*/

        this.actionQueue.push(async dispatch => {
          dispatch(failItem({
            ...this.selector,
            index,
            errorType: EventType.ErrorHowlPlay
          }));
        });
      },
      onplay: () => {
        // A stop can be queued while Howler is still finishing playback.
        // Starting playback again cancels that stale unload request.
        this.pendingUnloads.delete(index);
        const targetVolume = this.normalizeVolume(this.state?.volume ?? DefaultChannelVolume);

        songPlayer.fade(0.01, targetVolume,
          this.getRelativeFadeTime(0.0, targetVolume));

        // Disable media actions for media notifications
        navigator.mediaSession.setActionHandler('play', function () { });
        navigator.mediaSession.setActionHandler('pause', function () { });
        navigator.mediaSession.setActionHandler('seekto', function () { });
        navigator.mediaSession.setActionHandler('seekbackward', function () { });
        navigator.mediaSession.setActionHandler('seekforward', function () { });
        navigator.mediaSession.setActionHandler('previoustrack', function () { });
        navigator.mediaSession.setActionHandler('nexttrack', function () { });
        navigator.mediaSession.metadata = new MediaMetadata({
          title: item?.title,
          artist: item?.artist,
          artwork: [
            {
              src: logo,
              sizes: "400x400",
              type: "image/png",
            },
          ]
        })
      },
      onfade: () => {
        if (songPlayer.volume() === 0.0 && this.pendingUnloads.delete(index)) {
          this.actionQueue.push(async dispatch => {
            dispatch(unloadItem({
              ...this.selector,
              index
            }));
          });
        }
      },
      onend: () => {
        this.actionQueue.push(async dispatch => {
          if (this.getQueueItem(index)?.playing === true) {
            dispatch(stopItem({
              ...this.selector,
              index
            }));
          }

          dispatch(unloadItem({
            ...this.selector,
            index
          }));
        });
      }
    });

    this.songPlayers[index] = songPlayer;

    return songPlayer;
  }

  public override schedule(): void {
    if (this.state === undefined) {
      return;
    }

    // Check running players for necessary fadeouts
    for (const [indexStr, player] of Object.entries(this.songPlayers)) {
      const index = parseInt(indexStr);
      const item = this.getQueueItem(index);

      if (player.playing() && item?.playing === true) {
        if ((player.duration() - player.seek()) <= (this.getFadeTime() / 1000.0)) {
          this.actionQueue.push(async dispatch => {
            dispatch(stopItem({
              ...this.selector,
              index
            }));
          });
        }
      }
    }

    // Invoke local playback and playlist handling
    super.schedule();
  }

  public override play(index: number, fromPosition: number = 0): void {
    this.getHowl(index)!.seek(fromPosition || 0);
    this.getHowl(index)!.play();
  }

  public override stop(index: number): void {
    const howl = this.getHowl(index);
    if (!howl) {
      return;
    }

    this.pendingUnloads.add(index);
    howl.fade(howl.volume(), 0.0,
      this.getRelativeFadeTime(this.getHowl(index)!.volume(), 0.0));
  }

  public override unload(index: number): void {
    const howl = this.songPlayers[index];
    this.pendingUnloads.delete(index);
    if (!howl) {
      return;
    }

    howl.stop();
    howl.unload();

    /* eslint-disable @typescript-eslint/no-dynamic-delete */
    delete this.songPlayers[index];

    super.unload(index);
  }

  private allActiveHowls(): Howl[] {
    if (this.state === undefined) {
      return [];
    }

    const queue = this.state.queue;
    const playlist = queue.entries.slice(queue.playIndex);

    return playlist
      .map((_entry, index) => this.getHowl(queue.playIndex + index, false)!)
      .filter(howl => howl !== null);
  }

  public override changeVolume(prevVolume: number): ActionHandler | undefined {
    if (this.state === undefined) {
      return undefined;
    }

    const targetVolume = this.normalizeVolume(this.state.volume);

    this.allActiveHowls().forEach(howl => howl.volume(targetVolume));
  }

  public override mute(): void {
    this.allActiveHowls().forEach(howl => howl.mute(true));
  }

  public override unmute(): void {
    this.allActiveHowls().forEach(howl => howl.mute(false));
  }

  protected override cacheGet(key: string): string | null {
    if (Config.persistInLocalStorage) {
      return window.localStorage.getItem(key);
    }
    return null;
  }

  protected override cacheSet(key: string, data: string): boolean {
    if (Config.persistInLocalStorage) {
      try {
        window.localStorage.setItem(key, data);
        return true;
      } catch (e) {
        console.warn('Could not store song in local storage', e);
        window.localStorage.removeItem(key);
      }
    }
    return false;
  }
};
